/**
 * PostHog Analytics Integration
 *
 * This module initializes PostHog for user analytics and session recording.
 *
 * **Resilience to Ad Blockers / Privacy Tools:**
 *
 * PostHog is designed to handle being blocked gracefully:
 * - The library is bundled with the app, so `import posthog` always succeeds
 * - If network requests to eu.i.posthog.com are blocked (ad blocker, VPN, etc.):
 *   - The `init()` call succeeds (creates local instance)
 *   - Methods like `identify()`, `capture()` execute but fail silently
 *   - No errors are thrown, no console spam
 *   - The app continues working perfectly
 * - We don't wrap in try-catch because posthog.init() is synchronous and doesn't throw
 * - We don't log errors to Sentry for ad-blocker users (this is expected behavior)
 *
 * **Identity Tracking Fix (Nov 2025):**
 * - PR #8745 introduced a bug where `identify()` was never called
 * - This PR fixes initialization timing to ensure identity tracking works
 * - Uses `user.id` (not email) as distinct_id + explicit `alias()` call
 *
 * To enable tracking (GTM, Google Ads) in local development:
 *  export VITE_ORUS_LOCAL_DEV_ENABLE_TRACKING=true ./tools/dev.app.sh
 *
 */
import posthog from 'posthog-js'

import { isE2eEnvironment, isReviewOrProductionEnvironment, isTrackingEnabledInLocalDev } from './env-utils.ts'
import { logger } from './logger.ts'
import { getPostHogProject } from './posthog-project.ts'
import { serializeError } from './serialize-error.ts'

export function isPostHogEnabledInCurrentEnvironment(): boolean {
  return (isReviewOrProductionEnvironment() || isTrackingEnabledInLocalDev()) && !isE2eEnvironment()
}

let posthogInstance: typeof posthog | null = null
let initializationStarted = false
const onLoadedCallbacks: Array<() => void> = []

export function onPostHogLoaded(callback: () => void): void {
  if (posthogInstance && posthogInstance.get_session_id()) {
    callback()
  } else {
    onLoadedCallbacks.push(callback)
  }
}

export function getPostHogInstance(): typeof posthog | null {
  // If we already have a working instance, return it
  if (posthogInstance) return posthogInstance

  // Only attempt initialization once
  if (initializationStarted) return null
  initializationStarted = true

  // If PostHog is not enabled in the current environment, early return null
  if (!isPostHogEnabledInCurrentEnvironment()) {
    return null
  }

  posthog.init(getPostHogProject().apiKey, {
    api_host: 'https://eu.i.posthog.com',
    defaults: '2025-05-24',
    // person_profiles: 'always' is required to enable retroactive merging of anonymous events
    // into the identified Person when posthog.identify() is called after contract signing.
    // Without it, the SDK default ('identified_only') silently discards the pre-signing
    // anonymous history and creates a split Person — see IND-373.
    person_profiles: 'always',
    // Share the posthog cookie across the .orus.eu parent domain so www and app see the same $device_id
    // Without this each subdomain has its own anonymous identity and pre-funnel www activity stays orphan
    cross_subdomain_cookie: true,
    persistence: 'localStorage+cookie',
    capture_exceptions: true,
    loaded: () => {
      logger.info('PostHog initialized successfully')
      // Execute all pending callbacks
      onLoadedCallbacks.forEach((callback) => {
        try {
          callback()
        } catch (_err) {
          // ignore
        }
      })
      // Clear the list
      onLoadedCallbacks.length = 0
    },
    secure_cookie: isReviewOrProductionEnvironment(),
    // Enable session recording for all users
    session_recording: {
      recordCrossOriginIframes: true,
      // See `docs/session-replay-privacy.md`.
      blockSelector: '[data-pii]',
    },
  })

  // PostHog init is synchronous and returns a usable instance immediately
  // Methods like identify() and capture() queue events if not fully loaded yet
  posthogInstance = posthog

  logger.addTransport({
    error(error) {
      // The exception list keeps no custom field, so a TechnicalError context would be lost
      posthog.captureException(error, { errContext: serializeError(error)['errContext'] })
    },
    event() {
      // not handling simple events, they already go through the tracking module
    },
  })

  return posthog
}
